import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Users, MessageCircle, Video, Mic, ArrowDown, LogIn, UserPlus } from 'lucide-react';
import { AnimatedDemo } from '../components/AnimatedDemo';
import { ScenariosSection } from '../components/ScenariosSection';
import { AdvisorsSection } from '../components/AdvisorsSection';
import { signUp, signIn, signOut, supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';

export const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading, emailVerified } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<'signin' | 'signup'>('signup');
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: ''
  });

  // Handle navigation logic for authenticated users
  useEffect(() => {
    if (!loading && user && emailVerified) {
      // Check if user has completed onboarding by redirecting to onboarding
      // The onboarding page will handle checking completion status and redirect appropriately
      navigate('/boardroom');
    }
  }, [user, loading, emailVerified, navigate]);

  // If user is authenticated and email verified, show loading while redirecting
  if (!loading && user && emailVerified) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-900 via-black to-orange-900">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-orange-400 to-purple-500 mb-4">
            <Users className="w-8 h-8 text-white" />
          </div>
          <p className="text-white text-lg">Redirecting to your boardroom...</p>
        </div>
      </div>
    );
  }

  const handleGetStarted = () => {
    setAuthMode('signup');
    setShowAuthModal(true);
  };

  const handleSignIn = () => {
    setAuthMode('signin');
    setShowAuthModal(true);
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthError('');
    
    if (authMode === 'signup' && (!formData.email || !formData.password || !formData.fullName)) return;
    if (authMode === 'signin' && (!formData.email || !formData.password)) return;
    
    try {
      if (authMode === 'signup') {
        const { error } = await signUp(formData.email, formData.password, formData.fullName);
        if (error) throw error;
        // Show email verification message instead of navigating
        setShowAuthModal(false);
        setShowEmailVerification(true);
      } else {
        const { error } = await signIn(formData.email, formData.password);
        if (error) throw error;
        // Navigation will happen automatically via useAuth if email is verified
      }
    } catch (error: any) {
      // Handle specific error cases with user-friendly messages
      if (error.message?.includes('Invalid login credentials')) {
        if (authMode === 'signin') {
          setAuthError('Email or password is incorrect. Please check your credentials or try signing up if you don\'t have an account yet.');
        } else {
          setAuthError('Unable to create account. Please check your email and try again.');
        }
      } else if (error.message?.includes('Email not confirmed')) {
        setAuthError('Please check your email and click the confirmation link before signing in.');
      } else if (error.message?.includes('Password should be at least')) {
        setAuthError('Password must be at least 6 characters long.');
      } else if (error.message?.includes('User already registered')) {
        setAuthError('An account with this email already exists. Try signing in instead.');
      } else {
        setAuthError(error.message || 'Authentication failed. Please try again.');
      }
    } finally {
      setAuthLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-black to-orange-900">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-purple-400/20 via-transparent to-orange-400/20"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-orange-400/10 via-transparent to-purple-400/10"></div>
      </div>

      {/* Floating elements */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-gradient-to-r from-orange-400 to-purple-400 rounded-full opacity-60"
            initial={{ x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1000), y: Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 1000) }}
            animate={{
              x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1000),
              y: Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 1000),
            }}
            transition={{
              duration: 20 + i * 5,
              repeat: Infinity,
              repeatType: "reverse",
              ease: "linear"
            }}
          />
        ))}
      </div>

      <div className="relative z-10">
        {/* Navigation */}
        <nav className="flex justify-between items-center p-4 sm:p-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-r from-orange-400 to-purple-500 flex items-center justify-center">
              <Users className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
            <span className="text-white font-bold text-lg sm:text-xl">Advisory AI</span>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="https://bolt.new/"
              target="_blank"
              rel="noopener noreferrer"
              className="transition-all duration-300 hover:scale-105"
            >
              <img 
                src="/Built with Bolt.jpeg" 
                alt="Built with Bolt" 
                className="w-12 h-12 sm:w-14 sm:h-14 rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
              />
            </a>
            <button
              onClick={handleSignIn}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-white/20 transition-all duration-300"
            >
              <LogIn className="w-4 h-4" />
              <span className="hidden sm:inline">Sign In</span>
            </button>
          </div>
        </nav>

        {/* Hero Section */}
        <div className="flex flex-col items-center justify-center min-h-[calc(100vh-80px)] px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-6xl mx-auto"
          >
            {/* Logo/Brand */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mb-6 sm:mb-8"
            >
              <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-full bg-gradient-to-r from-orange-400 to-purple-500 mb-4 sm:mb-6">
                <Users className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 text-white" />
              </div>
            </motion.div>

            {/* Main Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="text-2xl sm:text-4xl md:text-5xl lg:text-7xl font-bold mb-3 sm:mb-4 md:mb-6 bg-gradient-to-r from-orange-400 via-purple-400 to-orange-400 bg-clip-text text-transparent leading-tight"
            >
              Your Personal Board of Directors.
            </motion.h1>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-semibold mb-4 sm:mb-6 text-white/90"
            >
              Available 24/7. On-Demand.
            </motion.h2>

            {/* Value Proposition */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="text-base sm:text-lg md:text-xl lg:text-2xl mb-6 sm:mb-8 md:mb-12 text-white/80 max-w-4xl mx-auto leading-relaxed px-2"
            >
              Get expert guidance from AI-powered advisors who think differently. Start with group chat, 
              escalate to live voice calls, or dive deep with 1:1 video sessions.
            </motion.p>

            {/* CTA Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="mb-6 sm:mb-8 md:mb-16"
            >
              <button
                onClick={handleGetStarted}
                className="group inline-flex items-center justify-center gap-2 sm:gap-3 px-6 sm:px-8 md:px-12 py-3 sm:py-4 md:py-6 rounded-full bg-gradient-to-r from-orange-500 to-purple-500 hover:from-orange-400 hover:to-purple-400 text-white font-bold text-base sm:text-lg md:text-xl transition-all duration-300 shadow-lg hover:shadow-2xl hover:shadow-orange-500/25 hover:scale-105 active:scale-95 w-auto min-w-[200px] sm:min-w-[250px]"
              >
                <UserPlus className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
                Get Started Free
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 group-hover:translate-x-1 transition-transform duration-300" />
              </button>
            </motion.div>

            {/* Capabilities */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6 mb-8 sm:mb-12 max-w-4xl mx-auto"
            >
              <div className="flex items-center justify-center gap-2 sm:gap-3 px-3 sm:px-4 md:px-6 py-2 sm:py-3 md:py-4 rounded-full bg-white/10 backdrop-blur-sm border border-white/20">
                <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 text-orange-400 flex-shrink-0" />
                <span className="text-white font-medium text-xs sm:text-sm md:text-base">Group & 1:1 Chat</span>
              </div>
              <div className="flex items-center justify-center gap-2 sm:gap-3 px-3 sm:px-4 md:px-6 py-2 sm:py-3 md:py-4 rounded-full bg-white/10 backdrop-blur-sm border border-white/20">
                <Mic className="w-4 h-4 sm:w-5 sm:h-5 text-orange-400 flex-shrink-0" />
                <span className="text-white font-medium text-xs sm:text-sm md:text-base">Live Voice Calls</span>
              </div>
              <div className="flex items-center justify-center gap-2 sm:gap-3 px-3 sm:px-4 md:px-6 py-2 sm:py-3 md:py-4 rounded-full bg-white/10 backdrop-blur-sm border border-white/20">
                <Video className="w-4 h-4 sm:w-5 sm:h-5 text-orange-400 flex-shrink-0" />
                <span className="text-white font-medium text-xs sm:text-sm md:text-base">1:1 Video Sessions</span>
              </div>
            </motion.div>
          </motion.div>

          {/* Scroll indicator - Hidden on mobile */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.5 }}
            className="absolute bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2 hidden sm:block"
          >
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="text-white/60 cursor-pointer"
              onClick={() => document.getElementById('demo')?.scrollIntoView({ behavior: 'smooth' })}
            >
              <ArrowDown className="w-6 h-6" />
            </motion.div>
          </motion.div>
        </div>

        {/* Animated Demo Section */}
        <div id="demo">
          <AnimatedDemo />
        </div>

        {/* Scenarios Section */}
        <ScenariosSection />

        {/* Meet Your Board Section */}
        <AdvisorsSection />

        {/* Final CTA */}
        <div className="px-4 py-12 sm:py-16 md:py-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-orange-400 to-purple-400 bg-clip-text text-transparent">
              Ready to Assemble Your Board?
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-white/80 mb-6 sm:mb-8 max-w-2xl mx-auto px-2">
              Join thousands who've transformed their decision-making with AI-powered advisory guidance.
            </p>
            
            <button
              onClick={handleGetStarted}
              className="group inline-flex items-center justify-center gap-2 sm:gap-3 px-6 sm:px-8 md:px-12 py-3 sm:py-4 md:py-6 rounded-full bg-gradient-to-r from-orange-500 to-purple-500 hover:from-orange-400 hover:to-purple-400 text-white font-bold text-base sm:text-lg md:text-xl transition-all duration-300 shadow-lg hover:shadow-2xl hover:shadow-orange-500/25 hover:scale-105 active:scale-95"
            >
              <UserPlus className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
              Start Free Today
              <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 group-hover:translate-x-1 transition-transform duration-300" />
            </button>
          </motion.div>
        </div>
      </div>

      {/* Authentication Modal */}
      {showAuthModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="w-full max-w-md bg-black/60 backdrop-blur-sm rounded-3xl p-6 sm:p-8 border border-white/20"
          >
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-r from-orange-400 to-purple-500 mb-4">
                {authMode === 'signup' ? <UserPlus className="w-6 h-6 text-white" /> : <LogIn className="w-6 h-6 text-white" />}
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">
                {authMode === 'signup' ? 'Create Your Account' : 'Welcome Back'}
              </h3>
              <p className="text-white/70">
                {authMode === 'signup' ? 'Get started with your personal board of directors' : 'Sign in to your advisory board'}
              </p>
            </div>

            <form onSubmit={handleAuth} className="space-y-4">
              {authMode === 'signup' && (
                <input
                  type="text"
                  value={formData.fullName}
                  onChange={(e) => setFormData(prev => ({ ...prev, fullName: e.target.value }))}
                  placeholder="Full Name"
                  className="w-full px-4 py-3 rounded-2xl bg-white/10 border border-white/20 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-orange-400"
                  required
                />
              )}
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="Email Address"
                className="w-full px-4 py-3 rounded-2xl bg-white/10 border border-white/20 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-orange-400"
                required
              />
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                placeholder="Password"
                className="w-full px-4 py-3 rounded-2xl bg-white/10 border border-white/20 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-orange-400"
                required
              />
              
              <div className="flex flex-col gap-3">
                {authError && (
                  <div className="p-3 rounded-xl bg-red-500/20 border border-red-500/40 text-red-200 text-sm">
                    {authError}
                  </div>
                )}
                
                <button
                  type="submit"
                  disabled={authLoading}
                  className={`w-full px-6 py-3 rounded-2xl font-semibold transition-all duration-300 ${
                    authLoading 
                      ? 'bg-white/10 text-white/50 cursor-not-allowed'
                      : 'bg-gradient-to-r from-orange-500 to-purple-500 text-white hover:from-orange-400 hover:to-purple-400'
                  }`}
                >
                  {authLoading ? 'Loading...' : (authMode === 'signup' ? 'Create Account' : 'Sign In')}
                </button>
                
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setAuthMode(authMode === 'signup' ? 'signin' : 'signup')}
                    className="text-white/70 hover:text-white transition-colors duration-300"
                  >
                    {authMode === 'signup' 
                      ? 'Already have an account? Sign in' 
                      : "Don't have an account? Sign up"
                    }
                  </button>
                </div>
                
                {authMode === 'signin' && authError?.includes('Email or password is incorrect') && (
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => {
                        setAuthMode('signup');
                        setAuthError('');
                      }}
                      className="text-orange-400 hover:text-orange-300 transition-colors duration-300 text-sm"
                    >
                      Create a new account instead
                    </button>
                  </div>
                )}
              </div>
            </form>

            <button
              onClick={() => setShowAuthModal(false)}
              className="absolute top-4 right-4 p-2 text-white/60 hover:text-white transition-colors duration-300"
            >
              ✕
            </button>
          </motion.div>
        </div>
      )}
      
      {/* Email Verification Modal */}
      {showEmailVerification && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="w-full max-w-md bg-black/60 backdrop-blur-sm rounded-3xl p-6 sm:p-8 border border-white/20"
          >
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-r from-green-400 to-blue-500 mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 7.89a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Check Your Email</h3>
              <p className="text-white/70 mb-6">
                We've sent a confirmation link to <strong>{formData.email}</strong>. 
                Please click the link in your email to verify your account and complete the signup process.
              </p>
              <div className="bg-orange-500/10 border border-orange-500/20 rounded-xl p-4 mb-6">
                <p className="text-orange-200 text-sm">
                  <strong>Note:</strong> You won't be able to access your account until you verify your email address. 
                  Check your spam folder if you don't see the email.
                </p>
              </div>
              <div className="flex flex-col gap-3">
                <button
                  onClick={async () => {
                    const { error } = await supabase.auth.resend({
                      type: 'signup',
                      email: formData.email,
                    });
                    if (!error) {
                      alert('Verification email resent!');
                    }
                  }}
                  className="w-full px-6 py-3 rounded-2xl bg-gradient-to-r from-orange-500 to-purple-500 text-white font-semibold hover:from-orange-400 hover:to-purple-400 transition-all duration-300"
                >
                  Resend Verification Email
                </button>
                <button
                  onClick={() => setShowEmailVerification(false)}
                  className="w-full px-6 py-3 rounded-2xl bg-white/10 text-white font-semibold hover:bg-white/20 transition-all duration-300"
                >
                  Close
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};